import { TELEPHONY_BASEURL } from './TelephonyProvider';

/**
 * REST client for the Telephony admin pages (Agents / Logs / Phone Numbers).
 * Cross-origin: the platform allowlists the CMS origins via CORS; auth is the
 * same API key the softphone dock uses.
 */
const API_KEY = process.env.REACT_APP_TELEPHONY_API_KEY || '';

export async function telephonyApi<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${TELEPHONY_BASEURL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(API_KEY ? { 'X-Api-Key': API_KEY } : {}),
      ...(options.headers || {}),
    },
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray((body as any).message)
      ? (body as any).message.join(', ')
      : (body as any).message || res.statusText;
    throw new Error(message);
  }
  return body as T;
}

export interface TelephonyAgent {
  id: string;
  externalId: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  dialTarget: string;
  region: string;
  isActive: boolean;
  whatsappCallingEnabled?: boolean;
  callMode: 'browser' | 'phone';
  team: { id: string; key: string; name: string };
}

export interface TelephonyNumber {
  id: string;
  e164: string;
  provider: string;
  country: string;
  purpose: string;
  label: string | null;
  isActive: boolean;
  whatsappEnabled?: boolean;
  lastUsedAt: string | null;
  team: { id: string; key: string; name: string } | null;
}

/**
 * Shape returned by the list endpoint (GET /v1/calls) — `recordings` there is
 * deliberately narrowed server-side (no transcript, no provider fields) so a
 * paginated listing stays cheap. See TelephonyCallDetail for the full row
 * shape returned by GET /v1/calls/:id.
 */
export interface TelephonyRecording {
  id: string;
  status: string;
  sentimentFlag?: boolean | null;
  /** One-sentence reason, set only when sentimentFlag is true. */
  sentimentReason?: string | null;
}

export interface TelephonyCall {
  direction: 'OUTBOUND' | 'INBOUND';
  channel?: 'VOICE' | 'WHATSAPP';
  customerUserId?: string | null;
  agent?: { externalId: string; name: string } | null;
  id: string;
  status: string;
  provider: string;
  callerIdE164: string;
  customerE164: string;
  agentId: string | null;
  source: string;
  sourceRecordId: string | null;
  talkSeconds: number | null;
  hangupCause: string | null;
  /** Who actually ended the call — null when the vendor gave no signal. */
  disconnectedBy: 'CUSTOMER' | 'AGENT' | 'SYSTEM' | null;
  createdAt: string;
  recordings: TelephonyRecording[];
}

/** True when this call has a recording flagged as a genuine red flag. */
export function callIsRedFlagged(call: Pick<TelephonyCall, 'recordings'>): boolean {
  return call.recordings.some((r) => r.sentimentFlag === true);
}

/** The reason text for the first flagged recording, for a tooltip. */
export function callRedFlagReason(call: Pick<TelephonyCall, 'recordings'>): string | null {
  return call.recordings.find((r) => r.sentimentFlag === true)?.sentimentReason ?? null;
}

/** Full recording row as returned by the detail endpoint (GET /v1/calls/:id). */
export interface TelephonyRecordingDetail {
  id: string;
  callId: string;
  provider: string;
  providerRecordingId: string;
  providerUrl: string | null;
  status: string;
  durationSec: number | null;
  format: string;
  transcriptStatus: 'PENDING' | 'STORED' | 'FAILED' | null;
  transcript: string | null;
  transcribedAt: string | null;
  sentimentFlag: boolean | null;
  sentimentReason: string | null;
  createdAt: string;
}

export interface TelephonyCallLeg {
  id: string;
  role: 'AGENT' | 'CUSTOMER';
  status: string;
  e164: string;
  ringingAt: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
  hangupCause: string | null;
}

/** Full call row as returned by GET /v1/calls/:id (ApiKeyGuard-protected). */
export interface TelephonyCallDetail {
  id: string;
  direction: 'OUTBOUND' | 'INBOUND';
  channel: 'VOICE' | 'WHATSAPP';
  status: string;
  provider: string;
  providerCallId: string | null;
  callerIdE164: string;
  customerE164: string;
  agentId: string | null;
  /** Null for an inbound call no agent was rung for. */
  teamId: string | null;
  source: string;
  sourceRecordId: string | null;
  customerUserId: string | null;
  initiatedAt: string | null;
  ringingAt: string | null;
  answeredAt: string | null;
  bridgedAt: string | null;
  endedAt: string | null;
  talkSeconds: number | null;
  billedSeconds: number | null;
  /** Prisma Decimal(12,6), serialized as a string — not a number. */
  costAmount: string | null;
  costCurrency: string | null;
  hangupCause: string | null;
  disconnectedBy: 'CUSTOMER' | 'AGENT' | 'SYSTEM' | null;
  disposition: string | null;
  createdAt: string;
  legs: TelephonyCallLeg[];
  recordings: TelephonyRecordingDetail[];
  agent: { id: string; name: string; externalId: string } | null;
  team: { id: string; key: string; name: string } | null;
}

/** True when any recording on this call is flagged as a genuine red flag. */
export function callDetailIsRedFlagged(call: Pick<TelephonyCallDetail, 'recordings'>): boolean {
  return call.recordings.some((r) => r.sentimentFlag === true);
}

export const TEAM_KEYS = ['sales', 'demo-ops', 'teacher-ops', 'cx', 'offline'];

/**
 * One row of GET /v1/analytics/calls. `agentId` is null on the row that holds
 * calls with no agent (unrouted inbound) — it is included so the agent-wise
 * table always sums to the overall totals.
 */
export interface TelephonyAgentAnalytics {
  agentId: string | null;
  agentExternalId: string | null;
  agentName: string | null;
  teamName: string | null;
  totalCalls: number;
  outboundCalls: number;
  inboundCalls: number;
  connectedCalls: number;
  /** Already a percentage to one decimal — do not multiply by 100 again. */
  connectivityPct: number;
  /** Summed over connected calls only. */
  talkSeconds: number;
  /** Per connected call; null when nothing connected in the range. */
  avgTalkSeconds: number | null;
  /**
   * Distinct customer numbers, not calls. Does NOT add up across agents — the
   * same customer reached by two agents counts once overall but once for each
   * of them, so never total this column client-side; the server's
   * `totals.uniqueNumbers` is the only correct overall figure.
   *
   * Optional because the CMS and the platform deploy independently — that
   * independence is the point of the integration, so the CMS is routinely the
   * newer of the two and has to read a field the platform may not send yet.
   * Rendering `undefined` as 0 is worse than useless here: a real 0 and "this
   * build doesn't report it" look identical, and 0 is the more believable of
   * the two, so a version skew reads as a factual answer. Render it as absent.
   */
  uniqueNumbers?: number;
}

export type TelephonyAnalyticsTotals = Omit<
  TelephonyAgentAnalytics,
  'agentId' | 'agentExternalId' | 'agentName' | 'teamName'
>;

export interface TelephonyCallAnalytics {
  range: { from: string | null; to: string | null };
  totals: TelephonyAnalyticsTotals;
  agents: TelephonyAgentAnalytics[];
}

/**
 * Calls the platform as *the signed-in user* rather than as the CMS, using the
 * same CMS-issued JWT tasksApi.ts sends to artium-tasks.
 *
 * The API key is deliberately NOT sent alongside it. The platform checks
 * X-Api-Key first and it wins when both are present, and a key caller has no
 * identity — so including it would silently discard the user and with it every
 * per-user rule the server applies. That is the trap AGENTS.md calls out:
 * adding an Authorization header while keeping the key changes nothing.
 *
 * Consequence: these endpoints need a valid CMS login, and a missing token is
 * an error rather than a quiet fall back to the key. Falling back would hand a
 * non-admin the unscoped, whole-organisation response, which is exactly what
 * the scoping exists to prevent.
 */
export async function telephonyUserApi<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const rawToken = localStorage.getItem('authToken') || '';
  if (!rawToken) {
    throw new Error('Your session has expired — please sign in again.');
  }
  const authHeader = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

  const res = await fetch(`${TELEPHONY_BASEURL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
      ...(options.headers || {}),
    },
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray((body as any).message)
      ? (body as any).message.join(', ')
      : (body as any).message || res.statusText;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status; // let callers branch on 401/403
    throw err;
  }
  return body as T;
}

/**
 * Aggregated call metrics for a date range. The platform does the counting —
 * paging /v1/calls and summing in the browser would need every row in the range.
 *
 * Sent as the signed-in user, because the platform scopes the response by who
 * is asking: a full ROLE_ADMIN sees everyone, anyone else sees only their own
 * agent and gets a 403 for naming someone else. Omit agentId/teamId entirely
 * as a non-admin — the server supplies the scope from the token.
 */
export function fetchCallAnalytics(filters: {
  from?: string;
  to?: string;
  agentId?: string;
  teamId?: string;
  direction?: string;
}): Promise<TelephonyCallAnalytics> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return telephonyUserApi<TelephonyCallAnalytics>(
    `/v1/analytics/calls${query ? `?${query}` : ''}`,
  );
}

/**
 * Seconds as something a human reads at a glance: "4h 12m", "7m 30s", "45s".
 * Hours are dropped once present (nobody reads "4h 12m 07s" off a card), and
 * null/zero render as an em dash rather than "0s" so an empty cell looks empty.
 */
export function formatTalkTime(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '—';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

/** Whole minutes, for the "total talk time" card where the unit is the headline. */
export function formatTalkMinutes(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '0';
  return Math.round(seconds / 60).toLocaleString('en-IN');
}

/** A ratio the backend already expressed as a percentage. */
export function formatPercent(pct: number | null | undefined): string {
  if (pct == null) return '—';
  return `${pct.toFixed(1)}%`;
}

/** Thousands separators, Indian grouping — matches the rest of the CMS. */
export function formatCount(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-IN');
}
